import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getPostsByCategory } from '../services/wordpress';
import { NewsItem } from '../types';
import { safeHTML, stripHTML } from '../utils/safeHTML';
import { Newspaper, CalendarDays, MapPin, ArrowRight, Loader2, Filter } from 'lucide-react';
import './NewsPage.css';

const getYearFromDateStr = (date?: string) => {
  if (!date) return '';
  const match = date.match(/\b(20\d{2})\b/);
  return match ? match[1] : '';
};

const truncateText = (html?: string, maxLength: number = 80) => {
  if (!html) return '';
  const stripped = html.replace(/<[^>]+>/g, '');
  if (stripped.length <= maxLength) return stripped;
  return stripped.substring(0, maxLength).trim() + '...';
};

const NewsPage: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [activities, setActivities] = useState<NewsItem[]>([]);
  const [branches, setBranches] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<string>('Semua');
  const navigate = useNavigate();

  // Load More states
  const [visibleNews, setVisibleNews] = useState(6);
  const [visibleActivities, setVisibleActivities] = useState(6);
  const [visibleBranches, setVisibleBranches] = useState(6);

  useEffect(() => {
    setVisibleNews(6);
    setVisibleActivities(6);
    setVisibleBranches(6);
  }, [selectedYear]);

  useEffect(() => {
    async function loadAllData() {
      // Fetch data categories parallel
      const [newsData, activityData, branchData] = await Promise.all([
        getPostsByCategory('berita', 30),
        getPostsByCategory('kegiatan', 30),
        getPostsByCategory('cabang', 30)
      ]);

      setNews(newsData);
      setActivities(activityData);
      setBranches(branchData);
      setLoading(false);
    }
    loadAllData();
  }, []);

  if (loading) {
    return (
      <div className="NewsPage_loadingState" role="status">
        <Loader2 className="NewsPage_spinner" size={48} />
        <span className="sr-only">Memuat berita dan kegiatan...</span>
      </div>
    );
  }

  // Get unique years
  const allYears = Array.from(new Set([
    ...news.map(item => getYearFromDateStr(item.date)),
    ...activities.map(item => getYearFromDateStr(item.date)),
    ...branches.map(item => getYearFromDateStr(item.date))
  ])).filter(Boolean).sort().reverse();

  // Filter data
  const filteredNews = selectedYear === 'Semua' ? news : news.filter(item => getYearFromDateStr(item.date) === selectedYear);
  const filteredActivities = selectedYear === 'Semua' ? activities : activities.filter(item => getYearFromDateStr(item.date) === selectedYear);
  const filteredBranches = selectedYear === 'Semua' ? branches : branches.filter(item => getYearFromDateStr(item.date) === selectedYear);

  return (
    <div className="NewsPage_page">
      <Helmet>
        <title>Berita & Agenda Terkini - Lakpesdam PBNU</title>
        <meta name="description" content="Pusat informasi, berita, dan agenda kegiatan strategis dari Lakpesdam PBNU dan cabang di seluruh Indonesia." />
        <meta property="og:title" content="Berita & Agenda Terkini - Lakpesdam PBNU" />
        <meta property="og:description" content="Pusat informasi, berita, dan agenda kegiatan strategis dari Lakpesdam PBNU." />
        <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://lakpesdam.or.id/berita" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Berita & Agenda Terkini - Lakpesdam PBNU",
            "description": "Pusat informasi, berita, dan agenda kegiatan strategis dari Lakpesdam PBNU dan cabang di seluruh Indonesia.",
            "url": "https://lakpesdam.or.id/berita"
          })}
        </script>
      </Helmet>
      <div className="container">
        
        {/* Header */}
        <div className="NewsPage_header">
          <h1 className="NewsPage_title">Pusat Informasi & Kegiatan</h1>
          <p className="NewsPage_subtitle">
            Dapatkan pembaruan terkini mengenai aktivitas, agenda strategis, dan perkembangan jaringan Lakpesdam di seluruh Indonesia.
          </p>
        </div>

        <div className="NewsPage_layout">
          
          {/* Sidebar Filter */}
          <aside className="NewsPage_sidebar">
            <div className="NewsPage_filterBox">
              <h3 className="NewsPage_filterTitle">
                <Filter size={18} />
                Filter Tahun
              </h3>
              <div className="NewsPage_filterList">
                <button 
                  className={`NewsPage_filterBtn ${selectedYear === 'Semua' ? 'active' : ''}`}
                  onClick={() => setSelectedYear('Semua')}
                >
                  Semua Tahun
                </button>
                {allYears.map(year => (
                  <button 
                    key={year}
                    className={`NewsPage_filterBtn ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="NewsPage_mainContent">
            
            {/* 1. SECTION BERITA */}
            {filteredNews.length > 0 && (
              <section className="NewsPage_section">
                <div className="NewsPage_sectionHeader">
                  <h2 className="NewsPage_sectionTitle">
                    <Newspaper className="NewsPage_sectionIcon" /> Berita Terkini
                  </h2>
                </div>
                <div className="NewsPage_grid">
                  {filteredNews.slice(0, visibleNews).map(item => (
                    <article 
                      key={item.id} 
                      className="NewsPage_newsCard"
                      onClick={() => navigate(`/${item.slug || item.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/${item.slug || item.id}`);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Baca berita: ${stripHTML(item.title)}`}
                    >
                      <div className="NewsPage_imgWrapper">
                        <img src={item.imageUrl} alt={stripHTML(item.title)} className="NewsPage_img" loading="lazy" width="400" height="250" />
                      </div>
                      <div className="NewsPage_content">
                        <div className="NewsPage_date">{item.date}</div>
                      {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                      <h3 className="NewsPage_cardTitle" {...safeHTML(item.title)} aria-label={stripHTML(item.title)} />
                        <div className="NewsPage_excerpt" {...safeHTML(truncateText(item.excerpt))} />
                        <span className="NewsPage_readMore">
                          Baca Selengkapnya <ArrowRight size={16} />
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
                {filteredNews.length > visibleNews && (
                  <div className="NewsPage_loadMoreContainer">
                    <button 
                      onClick={() => setVisibleNews(prev => prev + 6)}
                      className="NewsPage_loadMoreBtn"
                    >
                      Berita Lainnya <ArrowRight size={16} />
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* 2. SECTION KEGIATAN */}
            {filteredActivities.length > 0 && (
              <section className="NewsPage_section">
                <div className="NewsPage_sectionHeader">
                  <h2 className="NewsPage_sectionTitle">
                    <CalendarDays className="NewsPage_sectionIcon" /> Agenda & Kegiatan
                  </h2>
                </div>
                <div className="NewsPage_activityGrid">
                  {filteredActivities.slice(0, visibleActivities).map(item => {
                    const dateParts = item.date.split(' ');
                    const day = dateParts[0] || '01';
                    const month = dateParts[1]?.substring(0,3) || 'JAN';
                    const year = dateParts[2] || new Date().getFullYear().toString();
                    
                    return (
                      <article 
                        key={item.id} 
                        className="NewsPage_activityCard"
                        onClick={() => navigate(`/${item.slug || item.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/${item.slug || item.id}`);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Lihat kegiatan: ${stripHTML(item.title)}`}
                      >
                        <div className="NewsPage_dateBox">
                          <span className="NewsPage_dateDay">{day}</span>
                          <span className="NewsPage_dateMonth">{month}</span>
                          <span className="Activities_dateYear">{year}</span>
                        </div>
                        <div>
                        {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                        <h3 className="NewsPage_cardTitle" {...safeHTML(item.title)} aria-label={stripHTML(item.title)} />
                          <p className="NewsPage_activityExcerpt" {...safeHTML(truncateText(item.excerpt))} />
                          <span className="NewsPage_activityCategory">
                            {item.category}
                          </span>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {filteredActivities.length > visibleActivities && (
                  <div className="NewsPage_loadMoreContainer">
                    <button 
                      onClick={() => setVisibleActivities(prev => prev + 6)}
                      className="NewsPage_loadMoreBtn NewsPage_loadMoreBtn-amber"
                    >
                      Kegiatan Lainnya <ArrowRight size={16} />
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* 3. SECTION INFO CABANG */}
            {filteredBranches.length > 0 && (
              <section className="NewsPage_section">
                <div className="NewsPage_sectionHeader">
                  <h2 className="NewsPage_sectionTitle">
                    <MapPin className="NewsPage_sectionIcon" /> Kabar Daerah
                  </h2>
                </div>
                <div className="NewsPage_grid">
                  {filteredBranches.slice(0, visibleBranches).map(item => (
                    <article 
                      key={item.id} 
                      className="NewsPage_branchCard"
                      onClick={() => navigate(`/${item.slug || item.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/${item.slug || item.id}`);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Kabar daerah: ${stripHTML(item.title)}`}
                    >
                      <div className="NewsPage_branchLocation">
                        <MapPin size={14} /> {item.category || 'Nasional'}
                      </div>
                      {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                      <h3 className="NewsPage_cardTitle" {...safeHTML(item.title)} aria-label={stripHTML(item.title)} />
                      <div className="NewsPage_excerpt" {...safeHTML(truncateText(item.excerpt))} />
                      <div className="NewsPage_branchFooter">
                          <span className="NewsPage_branchDate">{item.date}</span>
                          <span className="NewsPage_branchLink">Lihat Detail</span>
                      </div>
                    </article>
                  ))}
                </div>
                {filteredBranches.length > visibleBranches && (
                  <div className="NewsPage_loadMoreContainer">
                    <button 
                      onClick={() => setVisibleBranches(prev => prev + 6)}
                      className="NewsPage_loadMoreBtn NewsPage_loadMoreBtn-slate"
                    >
                      Kabar Daerah Lainnya <ArrowRight size={16} />
                    </button>
                  </div>
                )}
              </section>
            )}
            
            {/* Show message if no data for selected year */}
            {filteredNews.length === 0 && filteredActivities.length === 0 && filteredBranches.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <p>Tidak ada informasi atau kegiatan di tahun {selectedYear}.</p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
